import { isCertva, resolvePoKDomain, resolvePoKName } from '../utils/domainUtils.js';
import { IMS_CERTIFICATION_URL } from '../constants.js';
import { INK, FG_2, FG_3, FONTS, LINE, PURPLE } from './sections/theme.js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useLocalizedPath } from '../hooks/useLocalizedPath.jsx';
import { whatsAppUrl } from '../utils/whatsapp.jsx';
import { RatingBadge } from './RatingBadge.jsx';
import { KYC_API } from '../endpoints.js';

const linkStyle = {
    color: FG_2,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '24px',
    fontFamily: FONTS.sans,
    textDecoration: 'none',
    transition: 'color 0.15s',
    '&:hover': { color: PURPLE },
};
const titleStyle = {
    color: INK,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '22px',
    fontFamily: FONTS.sans,
    mb: 1.5,
};
const mutedStyle = { color: FG_3, fontSize: 13, fontWeight: 400, fontFamily: FONTS.sans };

const COUNTRIES = ['us', 'ca', 'gb', 'de', 'es', 'tr', 'in', 'br', 'mx', 'co', 'ar', 'pe', 'cl', 'th'];
const BADGES = [
    { src: '/images/footer/iso.webp', alt: 'ISO 27001', href: 'https://trust.pok.tech/', h: 70, nofollow: true },
    {
        src: '/images/footer/logo-1edtech-cert.webp',
        alt: 'Open Badges 3.0 certified by 1EdTech',
        href: IMS_CERTIFICATION_URL,
    },
    {
        src: '/images/footer/holon.webp',
        alt: 'HolonIQ Latin America EdTech 100 2026',
        href: 'https://www.holoniq.com/notes/2026-latin-america-edtech-100',
    },
    { src: '/images/footer/elm.webp', alt: 'European Learning Model', href: null },
];
const SOCIAL = [
    {
        src: '/images/footer/social-linkedin.png',
        alt: 'LinkedIn',
        href: 'https://www.linkedin.com/company/pok-proof-of-knowledge/',
    },
    { src: '/images/footer/social-x.png', alt: 'X', href: 'https://x.com/POK_tech' },
    {
        src: '/images/footer/social-youtube.png',
        alt: 'YouTube',
        href: 'https://www.youtube.com/@POKProofofKnowledge',
    },
];

function FooterColumn({ title, links }) {
    return (
        <Stack
            sx={{
                alignItems: { xs: 'center', md: 'flex-start' },
                textAlign: { xs: 'center', md: 'left' },
            }}
        >
            <Typography sx={titleStyle}>{title}</Typography>
            <Stack spacing={1}>
                {links.map(({ label, href, external, nofollow }) => (
                    <Box
                        key={href}
                        component="a"
                        href={href}
                        {...(external
                            ? {
                                  target: '_blank',
                                  rel: nofollow ? 'noopener noreferrer nofollow' : 'noopener noreferrer',
                              }
                            : {})}
                        sx={{ ...linkStyle, display: 'block' }}
                    >
                        {label}
                    </Box>
                ))}
            </Stack>
        </Stack>
    );
}

export default function FooterContainer() {
    const { i18n, t } = useTranslation();
    const { faqs, blog, prices, dc, mc, ob, features, integrations, compareHub, podcast, team } = useLocalizedPath();
    const domain = resolvePoKDomain();
    const pokName = resolvePoKName();
    const kycApi = KYC_API;
    const country = i18n.language;
    const footerLogo = isCertva ? '/images/_certva_logo.png' : '/images/pok-logo.svg';
    const year = new Date().getFullYear();

    const platform = [
        { label: t('commons.navbar.prices'), href: prices() },
        { label: t('commons.navbar.dc'), href: dc() },
        { label: t('commons.navbar.mc'), href: mc() },
        { label: t('commons.navbar.ob'), href: ob() },
        { label: t('commons.navbar.features'), href: features() },
        { label: t('commons.navbar.integrations'), href: integrations() },
    ];
    const resources = [
        { label: t('i18nCommons.footer.blog'), href: blog() },
        { label: t('commons.navbar.podcast'), href: podcast() },
        { label: t('i18nCommons.footer.faqs'), href: faqs() },
        { label: t('commons.navbar.groups.comparativas'), href: compareHub() },
        { label: t('commons.navbar.team'), href: team() },
    ];
    const company = [
        {
            label: t('i18nCommons.footer.pok_email', { domain }),
            href: `mailto:${t('i18nCommons.footer.pok_email', { domain })}`,
            external: true,
        },
        { label: t('i18nCommons.footer.whatsapp'), href: whatsAppUrl(), external: true },
        {
            label: t('i18nCommons.footer.termAndConditions'),
            href: `${kycApi}/redirect/tc?country=${country}`,
            external: true,
            nofollow: true,
        },
        {
            label: t('i18nCommons.footer.privacyData'),
            href: `${kycApi}/redirect/pp?country=${country}`,
            external: true,
            nofollow: true,
        },
        {
            label: t('i18nCommons.footer.subprocessors'),
            href: `${kycApi}/redirect/subprocessors`,
            external: true,
            nofollow: true,
        },
    ];

    return (
        <Stack
            component="footer"
            sx={{
                position: 'relative',
                zIndex: 50,
                width: '100%',
                color: INK,
                backgroundColor: '#fff',
                borderTop: `1px solid ${LINE}`,
                padding: { xs: '2.5rem 1rem', md: '4rem 1.5rem 2rem' },
            }}
        >
            <RatingBadge />
            <Container maxWidth="lg">
                <Grid
                    container
                    spacing={{ xs: 4, md: 3 }}
                    sx={{
                        justifyContent: 'space-between',
                    }}
                >
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Stack
                            spacing={2}
                            sx={{
                                alignItems: { xs: 'center', md: 'flex-start' },
                                textAlign: { xs: 'center', md: 'left' },
                            }}
                        >
                            <Box component="a" href={`/${country}`}>
                                <img
                                    style={{ height: 32, width: 'auto', display: 'block' }}
                                    src={footerLogo}
                                    alt="POK"
                                    loading="lazy"
                                />
                            </Box>
                            <Typography sx={{ ...mutedStyle, color: FG_2, maxWidth: 320, lineHeight: 1.6 }}>
                                {t('i18nCommons.footer.subtitle')}
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={2.5}
                                sx={{
                                    pt: 0.5,
                                }}
                            >
                                {SOCIAL.map((s) => (
                                    <a key={s.alt} href={s.href} target="_blank" rel="noopener noreferrer">
                                        <img src={s.src} alt={s.alt} width={36} height={36} loading="lazy" />
                                    </a>
                                ))}
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2.4 }}>
                        <FooterColumn title={t('commons.navbar.sections.plataforma')} links={platform} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2.4 }}>
                        <FooterColumn title={t('i18nCommons.footer.resources')} links={resources} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2.6 }}>
                        <FooterColumn title={t('i18nCommons.footer.contact')} links={company} />
                    </Grid>
                </Grid>

                <Stack
                    direction="row"
                    spacing={4}
                    useFlexGap
                    sx={{
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        mt: { xs: 4, md: 6 },
                        mb: 3,
                    }}
                >
                    {BADGES.map((b) => {
                        const img = (
                            <img
                                src={b.src}
                                alt={b.alt}
                                style={{ height: b.h ?? 46, width: 'auto', opacity: 0.9 }}
                                loading="lazy"
                            />
                        );
                        return b.href ? (
                            <a
                                key={b.alt}
                                href={b.href}
                                target="_blank"
                                rel={b.nofollow ? 'noreferrer nofollow' : 'noreferrer'}
                            >
                                {img}
                            </a>
                        ) : (
                            <Box key={b.alt} sx={{ display: 'flex' }}>
                                {img}
                            </Box>
                        );
                    })}
                </Stack>

                <Box sx={{ borderTop: `1px solid ${LINE}`, pt: 2.5 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: { xs: 'center', md: 'flex-start' },
                        }}
                    >
                        <Typography sx={mutedStyle}>
                            © {year} {pokName} · {t('i18nCommons.footer.allRights')}
                        </Typography>
                        <Typography
                            sx={{ ...mutedStyle, maxWidth: { md: 620 }, textAlign: { xs: 'center', md: 'right' } }}
                        >
                            {t('i18nCommons.footer.offices')}{' '}
                            {COUNTRIES.map((c) => t(`i18nCommons.footer.country.${c}`)).join(' · ')}
                        </Typography>
                    </Stack>
                </Box>
            </Container>
        </Stack>
    );
}
